import { useEffect, useState, useRef } from 'react';
import { Typography } from '../typography';
import { Button } from '../button';
import { FileInput, Input } from '../input';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import { useCompleteRepairMutation, useUpdateImagesRepairMutation } from '../../hooks';
import { INPUT_ERRORS, MESSAGES } from '../../const';
import { ImageObjType, RepairUpdateImagesProps } from '../../types';
import { ModalImages } from './UploadImagesModal';
import { fullScreenButtonClick, HideInput, ImageGallery, ImageGalleryCard } from '../image-gallery';
import { filesFormatValidator } from '../../utils';
import { BorderLine } from '../border-line';
import { i18n } from '../../i18n';

interface RepairCompleteModalType {
  isSubmitClicked: boolean;
  submitClick: () => void;
  open: () => void;

  id: string;
  number: string;
  description: string;
  beforeImages: { url: string; name: string }[];
}

export const repairCompleteModal: RepairCompleteModalType = {
  isSubmitClicked: false,
  submitClick: () => null,
  open: () => null,
  id: '',
  number: '',
  description: '',
  beforeImages: [],
};

export const RepairCompleteModal = () => {
  const { mutate } = useCompleteRepairMutation();
  const updateImagesRepairMutation = useUpdateImagesRepairMutation();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [imageGalleryPhotos, setImageGalleryPhotos] = useState<any[]>([]);

  const [oldBeforeImages, setOldBeforeImages] = useState<RepairCompleteModalType['beforeImages']>(
    [],
  );
  const deleteOldBeforeImages = useRef<any[]>([]);
  const [newBeforeImages, setNewBeforeImages] = useState<any[]>([]);
  const [newBeforeImagesFiles, setNewBeforeImagesFiles] = useState<any[]>([]);
  const beforeImagesInputRef = useRef<any>();

  const [newAfterImages, setNewAfterImages] = useState<any[]>([]);
  const [newAfterImagesFiles, setNewAfterImagesFiles] = useState<any[]>([]);
  const afterImagesInputRef = useRef<any>();

  const [, setUpdateKey] = useState<any>();

  const validators = {
    beforeImages: ![...oldBeforeImages, ...newBeforeImages].length
      ? INPUT_ERRORS.REQUIRED.IMAGES
      : '',
    afterImages: !newAfterImages.length ? INPUT_ERRORS.REQUIRED.IMAGES : '',
  };

  useEffect(() => {
    repairCompleteModal.open = () => {
      setDescription(repairCompleteModal.description);
      setOldBeforeImages(repairCompleteModal.beforeImages);

      setNewBeforeImages([]);
      setNewBeforeImagesFiles([]);
      setNewAfterImages([]);
      setNewAfterImagesFiles([]);

      repairCompleteModal.isSubmitClicked = false;
      setOpen(true);
    };
  }, []);

  const handleDeleteImageClick = (i: number, imageCategory: RepairUpdateImagesProps['action']) => {
    if (imageCategory === 'before') {
      const isDeleteNewImage = i - oldBeforeImages.length >= 0;

      if (isDeleteNewImage) {
        i = i - oldBeforeImages.length;

        setNewBeforeImages(newBeforeImages.filter((_, index: number) => index !== i));
        setNewBeforeImagesFiles(newBeforeImagesFiles.filter((_, index: number) => index !== i));
      } else {
        deleteOldBeforeImages.current = [
          ...deleteOldBeforeImages.current,
          { action: 'delete', name: oldBeforeImages[i].name },
        ];

        setOldBeforeImages(oldBeforeImages.filter((_, index: number) => index !== i));
      }
    } else {
      setNewAfterImages(newAfterImages.filter((_, index: number) => index !== i));
      setNewAfterImagesFiles(newAfterImagesFiles.filter((_, index: number) => index !== i));
    }
  };

  const handleZoomClick = (i: number, imageCategory: RepairUpdateImagesProps['action']) => {
    let photos = [];

    if (imageCategory === 'before') {
      photos = [...oldBeforeImages.map(({ url }: any) => url), ...newBeforeImages];
    } else {
      photos = newAfterImages;
    }

    setImageGalleryPhotos(photos);
    setZoomImageIndex(i);
    fullScreenButtonClick('complete-modal');
  };

  const handleChangeFiles = (
    newImages: any,
    newFiles: any,
    imageCategory: RepairUpdateImagesProps['action'],
  ) => {
    if (imageCategory === 'before') {
      const newImagesValues = [...newBeforeImages, ...newImages];

      if (filesFormatValidator(newFiles, 'Image')) {
        enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
        return;
      }

      setNewBeforeImages(newImagesValues);
      setNewBeforeImagesFiles([...newBeforeImagesFiles, ...newFiles]);
      beforeImagesInputRef.current.type = 'text';
      beforeImagesInputRef.current.type = 'file';
    } else {
      const newImagesValues = [...newAfterImages, ...newImages];

      if (filesFormatValidator(newFiles, 'Image')) {
        enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
        return;
      }

      setNewAfterImages(newImagesValues);
      setNewAfterImagesFiles([...newAfterImagesFiles, ...newFiles]);
      afterImagesInputRef.current.type = 'text';
      afterImagesInputRef.current.type = 'file';
    }
  };

  const handleOnClose = () => {
    Object.keys(repairCompleteModal).forEach((key) => {
      if (key !== 'open') delete repairCompleteModal[key as keyof RepairCompleteModalType];
    });
    setOpen(!open);
  };

  const handleSubmitClick = async () => {
    repairCompleteModal.isSubmitClicked = true;
    setUpdateKey(Math.random());

    if (newBeforeImages.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }
    if (newAfterImages.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if ([...Object.values(validators).map((val) => !val)].some((val) => !val)) {
      return;
    }

    const mutateData = {
      id: repairCompleteModal.id,
    };

    const completeMutate = () => {
      mutate(mutateData, {
        onSuccess: () => {
          setOpen(false);
          repairCompleteModal.submitClick();
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_COMPLETED, 'error');
        },
      });
    };

    const beforeImagesData = newBeforeImages.map((img: any, i: any) => ({
      action: 'upload' as ImageObjType['action'],
      file: newBeforeImagesFiles[i],
    }));

    const beforeUpdateImages = () => {
      updateImagesRepairMutation.mutate(
        {
          id: repairCompleteModal.id,
          photos: [...beforeImagesData, ...deleteOldBeforeImages.current],
          action: 'before',
        },
        { onSuccess: () => completeMutate() },
      );
    };

    const afterImagesData = newAfterImages.map((img: any, i: any) => ({
      action: 'upload' as ImageObjType['action'],
      file: newAfterImagesFiles[i],
    }));

    updateImagesRepairMutation.mutate(
      {
        id: repairCompleteModal.id,
        photos: afterImagesData,
        action: 'after',
      },
      { onSuccess: () => beforeUpdateImages() },
    );
  };

  return (
    <>
      <ModalBase id={'repair-complete-modal'} open={open} onClose={handleOnClose}>
        <ModalWrapper>
          <Typography variant={'textL'} weight={'bold'}>
            {i18n.t('pages.modal.repairCompleteTitle')}: {repairCompleteModal.number}
          </Typography>
          <Input
            label={i18n.t('label.description')}
            data-size={'medium'}
            defaultValue={description}
            rows={4}
            multiline
            readOnly
          />
          <ModalBodyCustomColumns columns={'max-content 1fr max-content'}>
            <Typography
              variant={'textM'}
              weight={'bold'}
              color={
                repairCompleteModal.isSubmitClicked && validators.beforeImages
                  ? '--red-100'
                  : '--gray-100'
              }
            >
              {i18n.t('label.beforeImages')}*
            </Typography>
            <span />
            <Button
              size={'medium'}
              data-color={'primary'}
              onClick={() => beforeImagesInputRef.current.click()}
              justify={'left'}
            >
              {i18n.t('button.addBeforeImages')}
            </Button>
          </ModalBodyCustomColumns>
          <ModalImages isVisible>
            {[...oldBeforeImages, ...newBeforeImages].map((img, i) => (
              <ImageGalleryCard
                key={i}
                img={typeof img === 'string' ? img : img.url}
                handleZoomClick={() => handleZoomClick(i, 'before')}
                handleDeleteImageClick={() => handleDeleteImageClick(i, 'before')}
              />
            ))}
            <HideInput>
              <FileInput
                id={'file-input-before'}
                label={i18n.t('label.beforeImages')}
                inputRef={beforeImagesInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeFiles(newImages, newFiles, 'before')
                }
              />
            </HideInput>
          </ModalImages>
          <BorderLine space={'none'} />
          <ModalBodyCustomColumns columns={'max-content 1fr max-content'}>
            <Typography
              variant={'textM'}
              weight={'bold'}
              color={
                repairCompleteModal.isSubmitClicked && validators.afterImages
                  ? '--red-100'
                  : '--gray-100'
              }
            >
              {i18n.t('label.afterImages')}*
            </Typography>
            <span />
            <Button
              size={'medium'}
              data-color={'primary'}
              onClick={() => afterImagesInputRef.current.click()}
              justify={'left'}
            >
              {i18n.t('button.addAfterImages')}
            </Button>
          </ModalBodyCustomColumns>
          <ModalImages isVisible>
            {newAfterImages.map((img, i) => (
              <ImageGalleryCard
                key={i}
                img={typeof img === 'string' ? img : img.url}
                handleZoomClick={() => handleZoomClick(i, 'after')}
                handleDeleteImageClick={() => handleDeleteImageClick(i, 'after')}
              />
            ))}
            <HideInput>
              <FileInput
                id={'file-input-after'}
                label={i18n.t('label.afterImages')}
                inputRef={afterImagesInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                handleChangeFiles={(newImages, newFiles) =>
                  handleChangeFiles(newImages, newFiles, 'after')
                }
              />
            </HideInput>
          </ModalImages>
          <ModalFooterButtons>
            <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
              {i18n.t('button.cancel')}
            </Button>
            <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
              {i18n.t('button.submit')}
            </Button>
          </ModalFooterButtons>
        </ModalWrapper>
      </ModalBase>
      <ImageGallery id={'complete-modal'} photos={imageGalleryPhotos} startIndex={zoomImageIndex} />
    </>
  );
};
